"use client";

import {
  useCallback,
  useEffect,
  useId as useReactId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

/** SSR-safe layout effect. */
export const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Close on an outside click or Escape.
 *
 * `pointerdown` rather than `click`, so a menu closes on press rather than on
 * release; a release-based close lets a drag that started inside the menu and
 * ended outside it count as an outside click.
 */
export function useDismiss(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  open: boolean,
  close: () => void
): void {
  const list = Array.isArray(refs) ? refs : [refs];
  const stable = useRef(list);
  stable.current = list;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // A menu's submenu is portalled outside it, and a click there is still a click in the menu.
      if (target instanceof Element && target.closest(".pop-sub")) return;
      for (const ref of stable.current) {
        if (ref.current && ref.current.contains(target)) return;
      }
      close();
    };
    window.addEventListener("pointerdown", onPointer, true);
    return () => window.removeEventListener("pointerdown", onPointer, true);
  }, [open, close]);

  // Escape is the stack's business, not this hook's.
  useEscapeLayer(open, close);
}

/* ── the escape stack ──────────────────────────────────────────────────────
   Escape closes one thing: the innermost.

   Listeners alone cannot decide that. A popover inside a drawer has both
   listening, `stopPropagation` does not reach a listener on a different target,
   and pressing Escape once closes both — which is exactly the bug this exists
   to remove. So every dismissible layer takes a ticket on open, and only the
   layer holding the top ticket acts.
   ────────────────────────────────────────────────────────────────────────── */

const escapeStack: symbol[] = [];

export function useEscapeLayer(open: boolean, close: () => void): void {
  const ticket = useRef<symbol | null>(null);
  const stable = useRef(close);
  stable.current = close;

  useEffect(() => {
    if (!open) return;
    const mine = Symbol("layer");
    ticket.current = mine;
    escapeStack.push(mine);

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (escapeStack[escapeStack.length - 1] !== mine) return;
      event.preventDefault();
      stable.current();
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const index = escapeStack.indexOf(mine);
      if (index >= 0) escapeStack.splice(index, 1);
      ticket.current = null;
    };
  }, [open]);
}

export type Placement = "bottom-start" | "bottom-end" | "top-start" | "top-end";

export type AnchoredStyle = {
  left: number;
  top: number;
  minWidth?: number;
  maxHeight: number;
  side: "up" | "down";
};

/**
 * Position a floating element against an anchor, with collision handling.
 *
 * Fixed positioning against the viewport rather than absolute against the
 * anchor, so a popover inside an `overflow: hidden` panel is not clipped by
 * it. The measurement runs on open, on scroll and on resize, and flips the
 * popover above the anchor when there is more room there.
 */
export function useAnchored(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  open: boolean,
  options: { placement?: Placement; gap?: number; matchWidth?: boolean; minWidth?: number } = {}
): AnchoredStyle | null {
  const { placement = "bottom-start", gap = 6, matchWidth = false, minWidth } = options;
  const [style, setStyle] = useState<AnchoredStyle | null>(null);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = floating?.offsetWidth ?? rect.width;
    const height = floating?.offsetHeight ?? 260;
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const below = viewportHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    const wantsUp = placement.startsWith("top");
    // Flip only when the preferred side genuinely cannot hold it and the other
    // side can hold more. Flipping on every few pixels makes a menu jitter as
    // the page scrolls under it.
    const side: "up" | "down" = wantsUp ? (above < 180 && below > above ? "down" : "up") : below < 180 && above > below ? "up" : "down";

    const maxHeight = Math.max(150, (side === "down" ? below : above));

    let left = placement.endsWith("end") ? rect.right - width : rect.left;
    left = Math.min(left, viewportWidth - width - margin);
    left = Math.max(margin, left);

    const top = side === "down" ? rect.bottom + gap : Math.max(margin, rect.top - gap - Math.min(height, maxHeight));

    setStyle({
      left: Math.round(left),
      top: Math.round(top),
      minWidth: matchWidth ? Math.round(rect.width) : minWidth,
      maxHeight: Math.round(maxHeight),
      side,
    });
  }, [anchorRef, floatingRef, placement, gap, matchWidth, minWidth]);

  useIsomorphicLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    measure();
    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer && floatingRef.current) observer.observe(floatingRef.current);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      observer?.disconnect();
    };
  }, [open, measure, floatingRef]);

  return style;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Hold focus inside a container while it is open, and give it back on close.
 *
 * A dialog a keyboard can tab behind is not a dialog: the screen reader keeps
 * reading the page underneath and the Escape key is the only way out anybody
 * can find.
 */
// Signet, 2026-09-28: the same ticket idea as the escape stack. A confirm
// opened over a drawer is two traps at once, and when both answered Tab the
// drawer kept pulling focus back into itself, so a keyboard could never reach
// the confirm's Delete button. Only the newest trap acts now.
const trapStack: symbol[] = [];

export function useFocusTrap(ref: RefObject<HTMLElement | null>, open: boolean, autoFocus = true): void {
  const previous = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const mine = Symbol("trap");
    trapStack.push(mine);
    previous.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    if (node && autoFocus) {
      const first = node.querySelector<HTMLElement>("[data-autofocus]") ?? node.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? node).focus({ preventScroll: true });
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      if (trapStack[trapStack.length - 1] !== mine) return;
      const container = ref.current;
      if (!container) return;
      const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null || element === document.activeElement
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!container.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const index = trapStack.indexOf(mine);
      if (index >= 0) trapStack.splice(index, 1);
      previous.current?.focus?.({ preventScroll: true });
    };
  }, [ref, open, autoFocus]);
}

/** Stop the page behind a dialog scrolling, without the layout shifting. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [active]);
}

export type Hotkey = {
  key: string;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Fire even when a field has focus. Off by default. */
  inFields?: boolean;
};

function isTyping(target: EventTarget | null): boolean {
  const node = target as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || node.isContentEditable;
}

export function useHotkey(hotkey: Hotkey | Hotkey[], handler: (event: KeyboardEvent) => void): void {
  const list = useMemo(() => (Array.isArray(hotkey) ? hotkey : [hotkey]), [hotkey]);
  const stable = useRef(handler);
  stable.current = handler;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      for (const entry of list) {
        if (event.key.toLowerCase() !== entry.key.toLowerCase()) continue;
        const meta = event.metaKey || event.ctrlKey;
        if (Boolean(entry.meta) !== meta) continue;
        if (entry.shift !== undefined && entry.shift !== event.shiftKey) continue;
        if (entry.alt !== undefined && entry.alt !== event.altKey) continue;
        if (!entry.inFields && isTyping(event.target)) continue;
        event.preventDefault();
        stable.current(event);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list]);
}

/** Roving cursor for a list of options, driven by the keyboard. */
export function useListCursor(length: number, onCommit: (index: number) => void) {
  const [cursor, setCursor] = useState(0);
  useEffect(() => {
    setCursor((current) => (current >= length ? Math.max(0, length - 1) : current));
  }, [length]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setCursor((index) => (length === 0 ? 0 : (index + 1) % length));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setCursor((index) => (length === 0 ? 0 : (index - 1 + length) % length));
      } else if (event.key === "Home") {
        event.preventDefault();
        setCursor(0);
      } else if (event.key === "End") {
        event.preventDefault();
        setCursor(Math.max(0, length - 1));
      } else if (event.key === "Enter") {
        if (length === 0) return;
        event.preventDefault();
        onCommit(cursor);
      }
    },
    [length, cursor, onCommit]
  );

  return { cursor, setCursor, onKeyDown };
}

/** Keep the cursor row visible without scrolling the whole page. */
export function useScrollIntoView(containerRef: RefObject<HTMLElement | null>, index: number, open: boolean): void {
  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;
    const item = container.children[index] as HTMLElement | undefined;
    if (!item) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top < container.scrollTop) container.scrollTop = top - 4;
    else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight + 4;
    }
  }, [containerRef, index, open]);
}

/** Debounce a value. Used by search fields and availability checks. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * A stable id for aria wiring, identical on the server and the client.
 *
 * React's own, rather than a module counter: a counter renders a different
 * value on the server than on the client and hydration throws it away, which
 * silently breaks every label association it was supposed to create.
 */
export function useId(prefix = "u"): string {
  const id = useReactId();
  return `${prefix}${id.replace(/:/g, "")}`;
}

/** Ease a number towards its target, so a figure moves rather than blinks. */
export function useAnimatedNumber(target: number, duration = 620): number {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  const frame = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      from.current = target;
      setValue(target);
      return;
    }
    const start = from.current;
    if (start === target || !Number.isFinite(start)) {
      from.current = target;
      setValue(target);
      return;
    }
    const began = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - began) / duration);
      // Matches --ease-out closely enough to feel like one system.
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (target - start) * eased;
      from.current = current;
      setValue(current);
      if (progress < 1) frame.current = requestAnimationFrame(tick);
      else from.current = target;
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [target, duration]);

  return value;
}

/** Persist a small piece of UI state, surviving a reload. */
export function useStored<T>(
  key: string,
  initial: T,
  options: {
    /**
     * Forget the stored value if it was written longer ago than this.
     *
     * For a filter, coming back to a screen minutes later and finding it
     * still filtered is a small mystery — the list looks empty and the reason
     * is off-screen. Coming back within a second or two is somebody who
     * clicked the wrong tab, and throwing their filters away for that would
     * be worse. So it is kept briefly and then let go.
     */
    forgetAfterMs?: number;
  } = {}
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(initial);
  const loaded = useRef(false);
  const { forgetAfterMs } = options;

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return;
      const parsed = JSON.parse(raw) as { __at?: number; __v?: T } | T;
      if (forgetAfterMs && parsed && typeof parsed === "object" && "__at" in parsed) {
        const stamped = parsed as { __at: number; __v: T };
        if (Date.now() - stamped.__at > forgetAfterMs) {
          window.localStorage.removeItem(key);
          return;
        }
        setValue(stamped.__v);
        return;
      }
      setValue((parsed && typeof parsed === "object" && "__v" in parsed ? (parsed as { __v: T }).__v : parsed) as T);
    } catch {
      /* private mode, or a value from an older shape */
    }
  }, [key, forgetAfterMs]);

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(
          key,
          JSON.stringify(forgetAfterMs ? { __at: Date.now(), __v: next } : next)
        );
      } catch {
        /* storage full or blocked; the state still works for this session */
      }
    },
    [key, forgetAfterMs]
  );

  return [value, update];
}

/** True once the component has mounted, for anything that must not run on the server. */
export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = () => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Whether a node has been on screen yet.
 *
 * Once true it stays true: a figure that counts up every time it scrolls past
 * is a fidget, not a signal.
 */
export function useInView<T extends Element>(): [(node: T | null) => void, boolean] {
  const [seen, setSeen] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observer.current?.disconnect();
    if (!node || typeof IntersectionObserver === "undefined") {
      if (node) setSeen(true);
      return;
    }
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.current?.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px" }
    );
    observer.current.observe(node);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, seen];
}
